import { useState, useEffect, useCallback } from 'react';
import { customerDrayageAPI } from '../../services/api';

/**
 * CustomerDrayage — CRUD table for managing customer-specific drayage rate cards.
 * Follows the same inline-edit pattern as TruckingParameters.
 */
const CustomerDrayage = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm());

  function emptyAddForm() {
    return {
      customerName: '',
      pod: '',
      poe: '',
      distanceMiles: '',
      standardRate: '',
      heavyRate: '',
      notes: ''
    };
  }

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await customerDrayageAPI.getAll();
      setRates(result.data || []);
    } catch (err) {
      setError('Failed to load customer drayage rates');
      console.error('CustomerDrayage fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const handleEdit = (rate) => {
    setEditingId(rate.id);
    setEditForm({
      customerName: rate.customerName || '',
      pod: rate.pod || '',
      poe: rate.poe || '',
      distanceMiles: rate.distanceMiles ?? '',
      standardRate: rate.standardRate ?? '',
      heavyRate: rate.heavyRate ?? '',
      notes: rate.notes || ''
    });
    setFeedback(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFeedback(null);

      const data = {
        ...editForm,
        distanceMiles: editForm.distanceMiles !== '' ? Number(editForm.distanceMiles) : undefined,
        standardRate: editForm.standardRate !== '' ? Number(editForm.standardRate) : undefined,
        heavyRate: editForm.heavyRate !== '' ? Number(editForm.heavyRate) : undefined
      };

      await customerDrayageAPI.update(editingId, data);
      setFeedback({ type: 'success', message: 'Drayage rate updated successfully' });
      setEditingId(null);
      setEditForm({});
      await fetchRates();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to update drayage rate' });
      console.error('CustomerDrayage save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setFeedback(null);

      const data = {
        ...addForm,
        distanceMiles: addForm.distanceMiles !== '' ? Number(addForm.distanceMiles) : undefined,
        standardRate: addForm.standardRate !== '' ? Number(addForm.standardRate) : undefined,
        heavyRate: addForm.heavyRate !== '' ? Number(addForm.heavyRate) : undefined
      };

      await customerDrayageAPI.create(data);
      setFeedback({ type: 'success', message: 'Drayage rate created successfully' });
      setAddForm(emptyAddForm());
      setShowAddForm(false);
      await fetchRates();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to create drayage rate' });
      console.error('CustomerDrayage add error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading customer drayage rates...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Customer Drayage Rates</h1>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : 'Add Drayage Rate'}
          </button>
        </div>
      </div>

      {feedback && (
        <div className={feedback.type === 'success' ? 'success-banner' : 'error-message'}
          style={{ marginBottom: '1rem' }}>
          <p>{feedback.message}</p>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAddSubmit} className="detail-section" style={{ marginBottom: '1.5rem' }}>
          <h2>New Drayage Rate</h2>
          <div className="info-grid" style={{ gap: '0.75rem' }}>
            <div className="info-item">
              <label>Customer Name *</label>
              <input className="admin-inline-input" type="text" required
                value={addForm.customerName} onChange={(e) => handleAddChange('customerName', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Port of Discharge (POD) *</label>
              <input className="admin-inline-input" type="text" required
                value={addForm.pod} onChange={(e) => handleAddChange('pod', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Port of Entry (POE)</label>
              <input className="admin-inline-input" type="text"
                value={addForm.poe} onChange={(e) => handleAddChange('poe', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Distance (miles)</label>
              <input className="admin-inline-input admin-inline-narrow" type="number" step="0.1"
                value={addForm.distanceMiles} onChange={(e) => handleAddChange('distanceMiles', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Standard Rate ($) *</label>
              <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01" required
                value={addForm.standardRate} onChange={(e) => handleAddChange('standardRate', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Heavy Weight Rate ($)</label>
              <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                value={addForm.heavyRate} onChange={(e) => handleAddChange('heavyRate', e.target.value)} />
            </div>
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <input className="admin-inline-input" type="text"
                value={addForm.notes} onChange={(e) => handleAddChange('notes', e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Create Drayage Rate'}
            </button>
          </div>
        </form>
      )}

      {rates.length === 0 ? (
        <div className="empty-state">
          <p>No customer drayage rates configured yet.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="simple-table">
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>POD</th>
                <th>POE</th>
                <th>Distance (mi)</th>
                <th>Standard Rate</th>
                <th>Heavy Rate</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((rate) => (
                <tr key={rate.id}>
                  {editingId === rate.id ? (
                    <>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.customerName} onChange={(e) => handleChange('customerName', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.pod} onChange={(e) => handleChange('pod', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.poe} onChange={(e) => handleChange('poe', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input admin-inline-narrow" type="number" step="0.1"
                          value={editForm.distanceMiles} onChange={(e) => handleChange('distanceMiles', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                          value={editForm.standardRate} onChange={(e) => handleChange('standardRate', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                          value={editForm.heavyRate} onChange={(e) => handleChange('heavyRate', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.notes} onChange={(e) => handleChange('notes', e.target.value)} />
                      </td>
                      <td className="actions">
                        <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={handleCancel} disabled={saving}>
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{rate.customerName || '-'}</td>
                      <td>{rate.pod || '-'}</td>
                      <td>{rate.poe || '-'}</td>
                      <td>{rate.distanceMiles != null ? Number(rate.distanceMiles).toFixed(1) : '-'}</td>
                      <td>{rate.standardRate != null ? `$${Number(rate.standardRate).toFixed(2)}` : '-'}</td>
                      <td>{rate.heavyRate != null ? `$${Number(rate.heavyRate).toFixed(2)}` : '-'}</td>
                      <td className="text-small">{rate.notes || '-'}</td>
                      <td className="actions">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(rate)}>
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerDrayage;
